import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config';
import './Resy.css';

const API_ENDPOINT = config.API_ENDPOINT.replace(/\/$/, '');

function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Resy proxy responses forward upstream Resy payloads, which we haven't
// schema-verified yet. Pluck common-looking fields but never crash on absence.
function pick(obj, paths, fallback = '') {
  if (!obj) return fallback;
  for (const path of paths) {
    const value = path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return fallback;
}

function reservationRows(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];
  if (Array.isArray(data.reservations)) return data.reservations;
  if (Array.isArray(data.results)) return data.results;
  // Resy analytics report shapes: rows may live one level deeper, and
  // can be either an array of objects (ready to use) or columnar
  // [[v1,v2,...], ...] with a parallel `columns` array describing keys.
  const candidates = [
    data.data,
    data.report,
    data.payload,
  ];
  for (const c of candidates) {
    if (!c) continue;
    if (Array.isArray(c)) return c;
    if (Array.isArray(c.rows)) {
      return zipColumnsAndRows(c.columns, c.rows);
    }
    if (Array.isArray(c.records)) return c.records;
    if (Array.isArray(c.reservations)) return c.reservations;
  }
  if (Array.isArray(data.rows)) {
    return zipColumnsAndRows(data.columns, data.rows);
  }
  return [];
}

function zipColumnsAndRows(columns, rows) {
  if (!Array.isArray(rows)) return [];
  // Already row-of-objects — pass through unchanged.
  if (rows.length === 0 || (typeof rows[0] === 'object' && !Array.isArray(rows[0]))) {
    return rows;
  }
  if (!Array.isArray(columns)) return rows;
  const keys = columns.map((c) => {
    if (typeof c === 'string') return c;
    if (c && typeof c === 'object') return c.key || c.name || c.id || c.label;
    return undefined;
  });
  return rows.map((row) => {
    const out = {};
    row.forEach((value, i) => {
      const key = keys[i] || `col_${i}`;
      out[key] = value;
    });
    return out;
  });
}

function availabilitySlots(data) {
  // Resy /4/find responses commonly nest under results.venues[0].slots
  if (!data) return [];
  if (Array.isArray(data.slots)) return data.slots;
  const venues = data?.results?.venues;
  if (Array.isArray(venues) && venues.length > 0 && Array.isArray(venues[0].slots)) {
    return venues[0].slots;
  }
  return [];
}

/**
 * Extract the (first) venue object from a /4/find availability response.
 * Returns null if the response has no venues — used to drive the venue card
 * shown above the slot grid + empty state.
 */
function availabilityVenue(data) {
  const venues = data?.results?.venues;
  if (Array.isArray(venues) && venues.length > 0 && venues[0].venue) {
    return venues[0].venue;
  }
  return null;
}

/**
 * Pull turn times from the venue's default template, if present. Each entry
 * looks like { secs_amount: 5400, size: { min, max } }. Returns [] when
 * unavailable so callers don't have to guard.
 */
function availabilityTurnTimes(data) {
  const venue = availabilityVenue(data);
  if (!venue) return [];
  const tmplId = venue.default_template;
  const tmpl = tmplId && venue.templates?.[tmplId];
  return Array.isArray(tmpl?.turn_times) ? tmpl.turn_times : [];
}

/** Render an ISO date-time as "6:30 PM". Resy stores venue-local time without
 * a TZ offset, so we deliberately don't apply the browser's timezone. */
function formatSlotTime(iso) {
  if (typeof iso !== 'string') return '—';
  const m = iso.match(/T(\d{2}):(\d{2})/);
  if (!m) return iso;
  let h = parseInt(m[1], 10);
  const mm = m[2];
  const period = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${mm} ${period}`;
}

/** Format a turn-time seconds duration as "1h 30m". */
function formatTurnTime(secs) {
  if (typeof secs !== 'number') return '—';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

/** Render a party-size range as "2", "2–4", "5+", "up to 4", or "Any". */
function partySizeLabel(size) {
  if (!size || typeof size !== 'object') return 'Any';
  const min = typeof size.min === 'number' ? size.min : null;
  const max = typeof size.max === 'number' ? size.max : null;
  if (min !== null && min === max) return `Party of ${min}`;
  if (min !== null && max !== null) return `Party ${min}–${max}`;
  if (min !== null && max === null) return `Party ${min}+`;
  if (min === null && max !== null) return `Up to ${max}`;
  return 'Any party size';
}

const Resy = () => {
  const { getAuthToken } = useAuth();
  const [tab, setTab] = useState('reservations');

  const callResy = useCallback(async (api, method, params) => {
    const token = await getAuthToken();
    const res = await fetch(`${API_ENDPOINT}/v1/admin/resy`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: JSON.stringify({ api, method, params: params || {} }),
    });
    let payload = null;
    try {
      payload = await res.json();
    } catch (e) {
      const err = new Error(`Resy proxy returned non-JSON (HTTP ${res.status})`);
      err.code = 'PROXY_NON_JSON';
      throw err;
    }
    if (!payload || payload.success === false) {
      const err = new Error(payload?.error || `Resy proxy failed (HTTP ${res.status})`);
      // Preserve structured error code (e.g. RESY_NOT_CONFIGURED) so callers
      // can branch on integration state rather than parsing error strings.
      if (payload?.code) err.code = payload.code;
      throw err;
    }
    return payload.data;
  }, [getAuthToken]);

  return (
    <div className="resy-page">
      <div className="page-header">
        <h1>Resy</h1>
      </div>
      <div className="page-subtitle">
        Read-only view of reservations and availability via the Resy admin proxy.
      </div>

      <div className="resy-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={tab === 'reservations'}
          className={`resy-tab ${tab === 'reservations' ? 'active' : ''}`}
          onClick={() => setTab('reservations')}
        >
          Reservations
        </button>
        <button
          role="tab"
          aria-selected={tab === 'availability'}
          className={`resy-tab ${tab === 'availability' ? 'active' : ''}`}
          onClick={() => setTab('availability')}
        >
          Availability
        </button>
      </div>

      {tab === 'reservations' ? (
        <ReservationsTab callResy={callResy} />
      ) : (
        <AvailabilityTab callResy={callResy} />
      )}
    </div>
  );
};

// =====================
// Reservations tab
// =====================

const ReservationsTab = ({ callResy }) => {
  const [date, setDate] = useState(todayISO());
  const [status, setStatus] = useState('');
  const [venueId, setVenueId] = useState('');
  const [rows, setRows] = useState([]);
  const [rawList, setRawList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detail, setDetail] = useState(null);

  const [errorCode, setErrorCode] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setErrorCode(null);
    try {
      const params = { date };
      if (status) params.status = status;
      if (venueId) params.venue_id = venueId;
      const data = await callResy('reservations', 'list_reservations', params);
      setRawList(data);
      setRows(reservationRows(data));
    } catch (e) {
      setError(e.message || String(e));
      setErrorCode(e.code || null);
      setRows([]);
      setRawList(null);
    } finally {
      setLoading(false);
    }
  }, [callResy, date, status, venueId]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (row) => {
    const id = pick(row, ['reservation_id', 'id', 'resy_token']);
    if (!id) {
      setSelected(row);
      setDetail(null);
      setDetailError('Row has no reservation id field to fetch detail for.');
      return;
    }
    setSelected(row);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const data = await callResy('reservations', 'get_reservation', { reservation_id: String(id) });
      setDetail(data);
    } catch (e) {
      setDetailError(e.message || String(e));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelected(null);
    setDetail(null);
    setDetailError(null);
  };

  return (
    <>
      <div className="resy-toolbar">
        <div className="resy-field">
          <label htmlFor="resy-res-date">Date</label>
          <input
            id="resy-res-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="resy-field">
          <label htmlFor="resy-res-status">Status</label>
          <select
            id="resy-res-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="confirmed">Confirmed</option>
            <option value="canceled">Canceled</option>
            <option value="seated">Seated</option>
            <option value="completed">Completed</option>
            <option value="no_show">No-show</option>
          </select>
        </div>
        <div className="resy-field">
          <label htmlFor="resy-res-venue">Venue ID (optional)</label>
          <input
            id="resy-res-venue"
            type="text"
            placeholder="default"
            value={venueId}
            onChange={(e) => setVenueId(e.target.value)}
          />
        </div>
        <button className="btn btn-primary" onClick={load} disabled={loading || !date}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {errorCode === 'RESY_NOT_CONFIGURED' ? (
        <ResyNotConfiguredPanel />
      ) : error ? (
        <div className="resy-error">{error}</div>
      ) : null}

      {errorCode === 'RESY_NOT_CONFIGURED' ? null : loading ? (
        <div className="resy-loading">Loading reservations…</div>
      ) : rows.length === 0 ? (
        <div className="resy-empty">
          No reservations for {date}
          {rawList ? (
            <details className="resy-raw" style={{ marginTop: 12 }}>
              <summary>Raw response</summary>
              <pre>{JSON.stringify(rawList, null, 2)}</pre>
            </details>
          ) : null}
        </div>
      ) : (
        <div className="resy-table-wrap">
          <table className="resy-table">
            <thead>
              <tr>
                <th>Guest</th>
                <th>Party</th>
                <th>Time</th>
                <th>Status</th>
                <th>Venue</th>
                <th>ID</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const id = pick(row, ['reservation_id', 'id', 'resy_token'], `row-${i}`);
                return (
                  <tr key={String(id)} onClick={() => openDetail(row)}>
                    <td>
                      {pick(row, [
                        'guest.full_name',
                        'guest.name',
                        'user.full_name',
                        'name',
                      ], '—')}
                    </td>
                    <td>{pick(row, ['party_size', 'num_seats', 'covers'], '—')}</td>
                    <td>
                      {pick(row, [
                        'time_slot',
                        'reservation_time',
                        'start_time',
                        'day_time',
                      ], '—')}
                    </td>
                    <td>{pick(row, ['status', 'state'], '—')}</td>
                    <td>{pick(row, ['venue.name', 'venue_name', 'venue_id'], '—')}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{String(id)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <Drawer title="Reservation detail" onClose={closeDetail}>
          {detailLoading && <div className="resy-loading">Loading…</div>}
          {detailError && <div className="resy-error">{detailError}</div>}
          {detail && <DetailGrid object={detail} />}
          <details className="resy-raw" open={!detail}>
            <summary>Raw row</summary>
            <pre>{JSON.stringify(selected, null, 2)}</pre>
          </details>
          {detail && (
            <details className="resy-raw">
              <summary>Raw detail</summary>
              <pre>{JSON.stringify(detail, null, 2)}</pre>
            </details>
          )}
        </Drawer>
      )}
    </>
  );
};

// Rendered on the Reservations tab when the backend reports
// RESY_NOT_CONFIGURED — Resy's API key authenticated but returned its
// consumer SPA shell, meaning the ResyOS reservations endpoint paths
// haven't been wired up for this venue's tier yet. Pure explainer, no
// data fetching.
const ResyNotConfiguredPanel = () => (
  <div className="resy-notconfigured">
    <div className="resy-notconfigured-eyebrow">Integration pending</div>
    <h3>Reservations data isn't connected yet</h3>
    <p>
      Your Resy API key is valid (the Availability tab is working), but the
      ResyOS reservations endpoints haven't been configured for this venue.
      Resy issues these endpoint paths to onboarded partners on request.
    </p>
    <ul>
      <li>Ask your Resy partner contact for the ResyOS API docs (base URL +
        the "list reservations by day" endpoint).</li>
      <li>Engineering can plug those values into <code>RESY_API_BASE_URL</code>,
        <code> RESY_RESERVATIONS_LIST_PATH</code>, and
        <code> RESY_RESERVATION_DETAIL_PATH</code> without a code change.</li>
    </ul>
    <p className="resy-notconfigured-note">
      Until then, the Availability tab is the read-only Resy surface that
      works with this key.
    </p>
  </div>
);

// =====================
// Availability tab
// =====================

const AvailabilityTab = ({ callResy }) => {
  const [date, setDate] = useState(todayISO());
  const [partySize, setPartySize] = useState(2);
  const [venueId, setVenueId] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [slots, setSlots] = useState([]);
  const [rawSearch, setRawSearch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detail, setDetail] = useState(null);

  const search = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError(null);
    setSubmitted(true);
    try {
      const params = { date, party_size: Number(partySize) };
      if (venueId) params.venue_id = venueId;
      const data = await callResy('availability', 'search', params);
      setRawSearch(data);
      setSlots(availabilitySlots(data));
    } catch (e2) {
      setError(e2.message || String(e2));
      setSlots([]);
      setRawSearch(null);
    } finally {
      setLoading(false);
    }
  };

  const openSlot = async (slot) => {
    const configId = pick(slot, ['config.token', 'config.id', 'config_id', 'id']);
    setSelected(slot);
    setDetail(null);
    setDetailError(null);
    if (!configId) {
      setDetailError('Slot has no config token to fetch details.');
      return;
    }
    setDetailLoading(true);
    try {
      const data = await callResy('availability', 'slot_details', {
        config_id: String(configId),
        day: date,
        party_size: Number(partySize),
      });
      setDetail(data);
    } catch (e) {
      setDetailError(e.message || String(e));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeSlot = () => {
    setSelected(null);
    setDetail(null);
    setDetailError(null);
  };

  return (
    <>
      <form className="resy-toolbar" onSubmit={search}>
        <div className="resy-field">
          <label htmlFor="resy-av-date">Date</label>
          <input
            id="resy-av-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
          />
        </div>
        <div className="resy-field">
          <label htmlFor="resy-av-party">Party size</label>
          <input
            id="resy-av-party"
            type="number"
            min={1}
            max={20}
            value={partySize}
            onChange={(e) => setPartySize(e.target.value)}
            required
          />
        </div>
        <div className="resy-field">
          <label htmlFor="resy-av-venue">Venue ID (optional)</label>
          <input
            id="resy-av-venue"
            type="text"
            placeholder="default"
            value={venueId}
            onChange={(e) => setVenueId(e.target.value)}
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={loading || !date || !partySize}>
          {loading ? 'Searching…' : 'Search'}
        </button>
      </form>

      {error && <div className="resy-error">{error}</div>}

      {!submitted ? (
        <div className="resy-empty">Pick a date and party size, then Search.</div>
      ) : loading ? (
        <div className="resy-loading">Searching availability…</div>
      ) : (
        <>
          <VenueCard venue={availabilityVenue(rawSearch)} turnTimes={availabilityTurnTimes(rawSearch)} />
          {slots.length === 0 ? (
            <div className="resy-empty">
              <strong>No bookable slots</strong> for {date} (party of {partySize})
              {availabilityVenue(rawSearch) ? (
                <div className="resy-empty-hint">
                  Resy returned the venue but reports zero availability for this date. Check Resy
                  admin → Calendar to confirm the date is published and has open inventory.
                </div>
              ) : null}
            </div>
          ) : (
            <div className="resy-slot-grid">
              {slots.map((slot, i) => {
                const rawTime = pick(slot, ['date.start', 'start', 'time_slot', 'time'], '');
                const time = rawTime ? formatSlotTime(rawTime) : '—';
                const type = pick(slot, ['config.type', 'type'], '');
                const sizeMin = slot?.size?.min;
                const sizeMax = slot?.size?.max;
                const sizeLabel = (typeof sizeMin === 'number' || typeof sizeMax === 'number')
                  ? partySizeLabel(slot.size)
                  : '';
                const key = pick(slot, ['config.token', 'config.id', 'id'], `slot-${i}`);
                return (
                  <button
                    key={String(key)}
                    type="button"
                    className="resy-slot-card"
                    onClick={() => openSlot(slot)}
                  >
                    <div className="resy-slot-time">{time}</div>
                    {type ? <div className="resy-slot-meta">{String(type)}</div> : null}
                    {sizeLabel ? <div className="resy-slot-meta">{sizeLabel}</div> : null}
                  </button>
                );
              })}
            </div>
          )}
          {rawSearch ? (
            <details className="resy-raw" style={{ marginTop: 16 }}>
              <summary>Raw response</summary>
              <pre>{JSON.stringify(rawSearch, null, 2)}</pre>
            </details>
          ) : null}
        </>
      )}

      {selected && (
        <Drawer title="Slot detail" onClose={closeSlot}>
          {detailLoading && <div className="resy-loading">Loading…</div>}
          {detailError && <div className="resy-error">{detailError}</div>}
          {detail && <DetailGrid object={detail} />}
          <details className="resy-raw" open={!detail}>
            <summary>Raw slot</summary>
            <pre>{JSON.stringify(selected, null, 2)}</pre>
          </details>
          {detail && (
            <details className="resy-raw">
              <summary>Raw detail</summary>
              <pre>{JSON.stringify(detail, null, 2)}</pre>
            </details>
          )}
        </Drawer>
      )}
    </>
  );
};

// =====================
// Shared components
// =====================

/**
 * Summary card for a Resy venue — name, neighborhood/type/rating, key
 * identifiers (venue_id, slug), and turn times from the default template.
 * Designed to render above the slot grid so the operator immediately knows
 * which venue Resy resolved and what its booking shape looks like, even
 * when slots is empty.
 */
const VenueCard = ({ venue, turnTimes }) => {
  if (!venue) return null;
  const name = venue.name || 'Resy venue';
  const neighborhood = venue.location?.neighborhood;
  const venueType = venue.type;
  const rating = typeof venue.rating === 'number' ? venue.rating : null;
  const totalRatings = typeof venue.total_ratings === 'number' ? venue.total_ratings : null;
  const venueId = venue.id?.resy ?? venue.id;
  const slug = venue.url_slug;
  const tz = venue.location?.time_zone;

  return (
    <div className="resy-venue-card">
      <div className="resy-venue-head">
        <h4 className="resy-venue-name">{name}</h4>
        {rating !== null ? (
          <span className="resy-venue-rating" title={totalRatings !== null ? `${totalRatings} ratings` : undefined}>
            ★ {rating}{totalRatings ? ` (${totalRatings})` : ''}
          </span>
        ) : null}
      </div>
      {(neighborhood || venueType) ? (
        <div className="resy-venue-meta">
          {neighborhood ? <span>{neighborhood}</span> : null}
          {neighborhood && venueType ? <span aria-hidden="true"> · </span> : null}
          {venueType ? <span>{venueType}</span> : null}
        </div>
      ) : null}
      <div className="resy-venue-ids">
        {venueId != null ? (
          <span>venue_id <code>{String(venueId)}</code></span>
        ) : null}
        {slug ? (
          <>
            <span aria-hidden="true"> · </span>
            <span>slug <code>{slug}</code></span>
          </>
        ) : null}
        {tz ? (
          <>
            <span aria-hidden="true"> · </span>
            <span>tz <code>{tz}</code></span>
          </>
        ) : null}
      </div>
      {Array.isArray(turnTimes) && turnTimes.length > 0 ? (
        <div className="resy-venue-section">
          <div className="resy-venue-section-label">Turn times</div>
          <ul className="resy-venue-list">
            {turnTimes.map((t, i) => (
              <li key={i}>
                <span className="resy-venue-list-key">{partySizeLabel(t?.size)}</span>
                <span className="resy-venue-list-val">{formatTurnTime(t?.secs_amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

const Drawer = ({ title, onClose, children }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="resy-drawer-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="resy-drawer" role="dialog" aria-modal="true">
        <div className="resy-drawer-header">
          <h2>{title}</h2>
          <button className="resy-drawer-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="resy-drawer-body">{children}</div>
      </div>
    </div>
  );
};

const DetailGrid = ({ object }) => {
  if (!object || typeof object !== 'object') return null;
  const entries = Object.entries(object).filter(([, v]) => {
    if (v == null) return false;
    if (typeof v === 'object') return false;
    return true;
  });
  if (entries.length === 0) return null;
  return (
    <dl className="resy-detail-grid">
      {entries.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt>{k}</dt>
          <dd>{String(v)}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
};

export default Resy;
